<script lang="ts">
    import { isProd, environment } from '#lib/stores/stores.ts';
    import { currentUser, associatedClientSummaries } from '#api/auth.ts';
    import { isImpersonating, endImpersonation } from '#api/impersonation.ts';
    import { timeTravel } from '#lib/services/time.ts';
    import { Button } from '#comp/ui/button/index.ts';

    type Banner = {
        id: 'dev' | 'impersonation' | 'timeTravel' | 'mirroring';
        classes: string;
    };

    let currentClient = $derived($associatedClientSummaries?.find((c) => c.id === $currentUser?.clientId));
    let isMirroring = $derived(currentClient?.servicingMode === 'Mirroring');

    let travelLabel = $derived.by(() => {
        if (!$timeTravel.traveling || $timeTravel.frozenUtcMs === null) return '';
        const timeZone =
            typeof $currentUser?.timeZone === 'string' && $currentUser.timeZone.trim() !== ''
                ? $currentUser.timeZone
                : 'America/Phoenix';
        return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(
            $timeTravel.frozenUtcMs
        );
    });

    let current = $state(0);
    let paused = $state(false);
    let bannerEl = $state<HTMLDivElement | null>(null);
    let intervalId: ReturnType<typeof setInterval> | undefined;

    let banners = $derived.by(() => {
        const items: Banner[] = [];
        if (!$isProd) items.push({ id: 'dev', classes: 'border-b border-gray-300 bg-yellow-100' });
        if (isMirroring)
            items.push({
                id: 'mirroring',
                classes: 'border-b border-sky-500 bg-sky-200 font-semibold text-sky-900'
            });
        if ($timeTravel.traveling)
            items.push({
                id: 'timeTravel',
                classes: 'border-b border-violet-500 bg-violet-200 font-semibold text-violet-900'
            });
        if ($isImpersonating)
            items.push({ id: 'impersonation', classes: 'border-b border-red-400 bg-red-100 text-red-900' });
        return items;
    });

    $effect(() => {
        if (current >= banners.length) current = 0;
    });

    $effect(() => {
        clearInterval(intervalId);
        if (banners.length > 1 && !paused) {
            intervalId = setInterval(() => {
                current = (current + 1) % banners.length;
            }, 5000);
        }
        return () => clearInterval(intervalId);
    });

    // Publish the banner's live height so the AppBar, --chrome-bottom, and the
    // portal header can offset against it without hard-coding the banner height.
    // Measured via getBoundingClientRect (sub-pixel exact) rather than offsetHeight
    // (integer-rounded) — the rounding left a hairline gap under a fixed banner.
    $effect(() => {
        if (banners.length === 0 || !bannerEl) {
            document.documentElement.style.setProperty('--banner-h', '0px');
            return;
        }
        const el = bannerEl;
        const update = () =>
            document.documentElement.style.setProperty('--banner-h', `${el.getBoundingClientRect().height}px`);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => {
            observer.disconnect();
            document.documentElement.style.setProperty('--banner-h', '0px');
        };
    });

    function prev() {
        current = (current - 1 + banners.length) % banners.length;
    }

    function next() {
        current = (current + 1) % banners.length;
    }
</script>

{#if banners.length > 0}
    {@const banner = banners[current]}
    <div
        bind:this={bannerEl}
        class="fixed top-0 left-0 z-[60] flex w-full items-center py-2 text-sm shadow-xs {banner.classes}"
        role="status"
        onmouseenter={() => (paused = true)}
        onmouseleave={() => (paused = false)}>
        {#if banners.length > 1}
            <button
                class="ml-2 px-1 text-lg leading-none font-bold opacity-70 hover:opacity-100"
                aria-label="Previous banner"
                onclick={prev}>
                &#8249;
            </button>
        {/if}

        <div class="flex min-w-0 flex-1 items-center justify-center gap-1 px-2">
            {#if banner.id === 'dev'}
                <span class="truncate">🚧 This site is running in {$environment} mode</span>
            {:else if banner.id === 'mirroring'}
                <span class="truncate">
                    🪞 MIRROR — {currentClient?.name} is mirrored from its primary servicing system
                </span>
            {:else if banner.id === 'timeTravel'}
                <span class="truncate">⏳ Time traveling — this client's clock is set to {travelLabel}</span>
            {:else if banner.id === 'impersonation'}
                <span class="min-w-0 truncate">
                    Impersonating <strong>{$currentUser?.displayName}</strong>
                </span>
                <Button
                    size="sm"
                    variant="link"
                    class="h-auto shrink-0 p-0 text-red-900 underline"
                    onclick={endImpersonation}>
                    End Impersonation
                </Button>
            {/if}
        </div>

        {#if banners.length > 1}
            <div class="mr-2 flex items-center gap-1">
                {#each banners as _, i}
                    <span
                        class="inline-block h-1.5 w-1.5 rounded-full {i === current
                            ? 'bg-current opacity-70'
                            : 'bg-current opacity-25'}">
                    </span>
                {/each}
            </div>
            <button
                class="mr-2 px-1 text-lg leading-none font-bold opacity-70 hover:opacity-100"
                aria-label="Next banner"
                onclick={next}>
                &#8250;
            </button>
        {/if}
    </div>
{/if}
